import {
	Component,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
	CarrierFeedback,
	CarrierTable,
	ContactsTable,
	DIFFERENCE_IN_DAYS,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import {
	ACCOUNT_TYPE,
	CARGOCHIEF_KEYS,
	CARRIER_FEEDBACK_SETTINGS,
	INDEXED_DB,
	PLAN_NAMES,
	S3_CSV_TEMPLATE,
	URLS,
} from '@domain/constants/app-variables';
import { TEMPLATE_TYPE } from '@domain/constants/email-templates';
import { UtilService } from '@service/util/util.service';
import { SharedService } from '@service/shared/shared.service';
import { IndexedDBService } from '@service/indexedDB/indexed-db.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { IntercomService } from '@service/intercom/intercom.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { LaneDetailsComponent } from '../../carrier-full-profile/lane-details/lane-details.component';
import { SearchPipe } from '@shared/search.pipe';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { environment } from '@environment/environment';
import { QuickSearchComponent } from '../quick-search/quick-search.component';
import { HeaderService } from '@service/header/header.service';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { ProfilePopoverComponent } from '../profile-popover/profile-popover.component';
import { Subscription } from 'rxjs';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { BrokerHelperService } from '@service/brokerHelper/broker-helper.service';
import { Store } from '@ngrx/store';
import { SessionActions } from '@state/session';
import { ProductsService } from '@service/products/products.service';
import { HeaderMenu, buildMenuOptions } from './menu-utils';
import { CacheService } from '@service/cache/cache.service';
import { NotificationPopoverComponent } from '../notification-popover/notification-popover.component';
import { BaseComponent } from '@shared/base/base.component';
import { CookieService } from 'ngx-cookie-service';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [SearchPipe, CookieService],
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() showLogout: boolean = false;
	@Input() showSignup: boolean = false;
	@Input() showSignin: boolean = false;
	user;
	URLS = URLS;
	ACCOUNT_TYPE = ACCOUNT_TYPE;
	isAdminOrOwner: boolean;
	isFullWidthLayout = false;

	IS_CARRIERS_ENABLED: boolean = false;
	FREE_TRIAL_DAYS_REMAINING = null;

	isCarrierFullProfileOpened: boolean = false;
	carrierLaneData: any = null;
	contactList: ContactsTable[] = null;
	recentFeedbackList: Array<CarrierFeedback> = null;
	@ViewChild('laneDetails') laneDetails: LaneDetailsComponent;
	@ViewChild('quickSearchComponent') quickSearchComponent: QuickSearchComponent;
	@ViewChild('profilePopover') profilePopoverComponent: ProfilePopoverComponent;
	@ViewChild('notificationPopover', { static: false })
	notificationsPopoverComponent: NotificationPopoverComponent;
	S3_CSV_TEMPLATE = S3_CSV_TEMPLATE;

	USER_ACCOUNT_TYPE: string = null;
	showCapacitySearchNotification: boolean = false;
	isUploadingFile: boolean = false;

	environment = environment;
	highlightLandingPageSection: boolean = false;
	private openProfilePanelSubscription: Subscription;
	private openNotificationPopoverWithPanelExpandedSubscription: Subscription;

	TEMPLATE_TYPE = TEMPLATE_TYPE;

	MENU_OPTIONS!: HeaderMenu;

	constructor(
		private router: Router,
		private restService: RestService,
		private urlService: UrlService,
		public cargochief: Cargochief,
		public utilService: UtilService,
		public sharedService: SharedService,
		private indexedDB: IndexedDBService,
		private segmentWrapper: SegmentWrapperService,
		public intercomService: IntercomService,
		public accountSettings: AccountSettingsService,
		private activatedRoute: ActivatedRoute,
		private headerService: HeaderService,
		public projectHelperService: ProjectHelperService,
		public brokerHelperService: BrokerHelperService,
		private store: Store,
		public productsService: ProductsService,
		private cacheService: CacheService,
		private cookieService: CookieService
	) {
		super();
	}

	ngOnInit() {
		this.user = this.cargochief.getLoginSetupUser();
		this.USER_ACCOUNT_TYPE = this.cargochief.USER_ACCOUNT_TYPE;
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
		const planName: string = this.utilService.getUserPlanName();
		// this.initializeSidenavLayout();
		if (
			this.utilService.isAccountTypeBroker() ||
			this.utilService.isAccountTypeShipper() ||
			(IS_NOT_NULL_OR_UNDEFINED(planName) &&
				planName.toLowerCase() == PLAN_NAMES.PRICING.toLowerCase()) ||
			this.utilService.isAccountTypeCarrier()
		) {
			this.isFullWidthLayout = true;
		}
		this.layoutSwitched();
		this.IS_CARRIERS_ENABLED = this.isCarriersEnabled();

		this.sharedService.getfeedbackNotificationEvent().subscribe((data: any) => {
			if (IS_NOT_NULL_OR_UNDEFINED(data)) {
				this.showCapacitySearchNotification = data;
			}
		});
		this.activatedRoute.queryParams.subscribe((params) => {
			if (IS_NULL_OR_UNDEFINED(params.capacitySearch)) {
				this.showCapacitySearchNotification = false;
			}
		});
		if (IS_NULL_OR_UNDEFINED(this.openProfilePanelSubscription)) {
			this.openProfilePanelSubscription = this.headerService
				.getOpenProfilePanelEvent()
				.pipe(take(1))
				.subscribe(() => {
					this.openProfilePanel();
				});
		}
		if (IS_NULL_OR_UNDEFINED(this.openNotificationPopoverWithPanelExpandedSubscription)) {
			this.openNotificationPopoverWithPanelExpandedSubscription = this.headerService
				.getOpenNotificationsPanelEvent()
				.pipe(takeUntil(this.isDestroyed$), distinctUntilChanged())
				.subscribe(({ panelToOpen }) => {
					this.openNotificationPopover({ panelToOpen });
				});
		}

		// set menu visible
		this.MENU_OPTIONS = buildMenuOptions(
			this.isPaymentView(),
			this.showLogout,
			this.isFullWidthLayout,
			this.IS_CARRIERS_ENABLED,
			this.utilService,
			this.productsService,
			this.accountSettings
		);

		if (this.user) {
			this.cacheService.updateCache();
		}
	}

	isUserLoggedIn() {
		return !this.utilService.isNullOrUndefined(this.cargochief.getUser());
	}

	logout() {
		const url = this.urlService.logoutUrl();
		this.restService.POST(url, {}).subscribe(
			(result) => {
				this.endSession();
				this.cookieService.deleteAll();
			},
			(error) => {
				this.endSession();
			}
		);
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.logged_out, {});
	}

	endSession() {
		this.store.dispatch(SessionActions.logout.start());
		this.indexedDB.updateShowDashboardSetting(true);
		localStorage.clear();
		this.unsubscribeAllObservables();
		this.router.navigate(['/login']);
	}

	isDemoAccount() {
		return this.accountSettings.DEMO_ACCOUNT;
	}

	layoutSwitched() {
		this.cargochief.setItem({ [CARGOCHIEF_KEYS.LAYOUT_SETTING]: this.isFullWidthLayout });
		this.sharedService.sendLayoutToggled(this.isFullWidthLayout);
		if (!this.utilService.isNullOrUndefined(this.indexedDB.application.indexedDB.db)) {
			this.indexedDB.updateLayoutSetting(this.isFullWidthLayout);
		}
	}

	initializeSidenavLayout() {
		if (this.utilService.isAccountTypeBroker() || this.utilService.isAccountTypeShipper()) {
			if (!this.utilService.isNullOrUndefined(this.cargochief.getLayoutSetting())) {
				this.isFullWidthLayout =
					this.cargochief.getLayoutSetting() == 'true'
						? JSON.parse(this.cargochief.getLayoutSetting())
						: false;
				this.layoutSwitched();
			}
			if (!this.utilService.isNullOrUndefined(this.indexedDB.application.indexedDB.db)) {
				this.indexedDB.getAll(INDEXED_DB.LAYOUT).subscribe((items: any) => {
					if (
						!this.utilService.isNullOrUndefined(items) &&
						!this.utilService.isNullOrUndefined(items[0])
					) {
						this.isFullWidthLayout = items[0];
						this.layoutSwitched();
					}
				});
			}
		}
	}

	showSettings(): boolean {
		if (!this.utilService.isNullOrUndefined(this.cargochief.getAccount())) {
			return this.productsService.CARRIER_RECRUITING;
		} else {
			return false;
		}
	}

	isProjectsEnabled() {
		return (
			this.productsService.PROJECT_CARRIERS &&
			this.showLogout &&
			this.isFullWidthLayout &&
			this.isNotAdmin()
		);
	}

	isPaymentView(): boolean {
		return window.location.href.indexOf('/#/register/complete_payment') > 0;
	}

	isCarriersEnabled(): boolean {
		return (
			this.showLogout &&
			this.isFullWidthLayout &&
			this.isNotAdmin() &&
			this.productsService.CARRIER_PROFILES &&
			!this.isPaymentView()
		);
	}

	isNotAdmin() {
		return (
			this.utilService.isAccountTypeBroker() ||
			this.utilService.isAccountTypeShipper() ||
			this.utilService.isAccountTypeCarrier()
		);
	}

	isFreeTrial(): boolean {
		const account = this.cargochief.getAccount();
		if (
			!this.utilService.isNullOrUndefined(account) &&
			!this.utilService.isNullOrUndefined(account.trialEnd) &&
			!this.utilService.isNullOrUndefined(account.cancelAtPeriodEnd) &&
			!account.cancelAtPeriodEnd
		) {
			this.FREE_TRIAL_DAYS_REMAINING = DIFFERENCE_IN_DAYS(
				new Date(),
				new Date(account.trialEnd * 1000)
			);
			if (this.FREE_TRIAL_DAYS_REMAINING <= 14 && this.FREE_TRIAL_DAYS_REMAINING >= 0) {
				return true;
			}
		}
		return false;
	}

	isManageUsersEnabled() {
		return this.productsService.MULTIPLE_USERS;
	}

	shutdownIntercom() {
		this.intercomService.shutdown();
	}

	openCarrierFullProfile(carrier: CarrierTable) {
		this.cargochief.setItem({ [CARGOCHIEF_KEYS.IS_FULL_PROFILE_OPENED]: true });
		this.carrierLaneData = null;
		this.contactList = null;
		this.recentFeedbackList = null;
		this.isCarrierFullProfileOpened = true;
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_viewed_full_profile, carrier);
		this.getCarrierContacts(carrier);
		this.getRecentFeedbackList(carrier.id);
	}

	getCarrierContacts(carrier: CarrierTable) {
		const contactsUrl = this.urlService.contactsUrl(carrier.id);
		this.restService.GET(contactsUrl).subscribe(
			(result) => {
				this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.contact_requested, result);
				this.contactList = result['data'];
				this.carrierLaneData = {
					lane: {
						businessName: carrier.businessName,
						customerCarrier: carrier.customerCarrier,
						firmMc: carrier.mc,
						firmDot: carrier.dot,
						powerUnits: carrier.powerUnits,
						drivers: carrier.drivers,
						carrierId: carrier.id,
						carrierHQ: carrier.carrierHQ,
						active: carrier.active,
						vettingSource: carrier.vettingSource,
						carrierOnboardedOn: carrier.carrierOnboardedOn,
						ccBookingRateDisplay: carrier.ccBookingRateDisplay,
						ccEngagementRateDisplay: carrier.ccEngagementRateDisplay,
						ccFalloffRateDisplay: carrier.ccFalloffRateDisplay,
						ccTotalBookedLoads: carrier.ccTotalBookedLoads,
						ccScore: carrier.ccScore,
					},
					contactList: this.contactList,
				};
			},
			(error) => {
				this.utilService.showSnackbar(
					'Cannot fetch contact list.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.contactList = [];
			}
		);
	}

	getRecentFeedbackList(id: string) {
		const url = this.urlService.getCarrierFeedback(id, {
			pageSize: CARRIER_FEEDBACK_SETTINGS.PAGE_SIZE,
		});
		this.restService.GET(url).subscribe((response) => {
			this.recentFeedbackList = response['data'];
		});
	}

	closeLaneDetails(laneDetails: LaneDetailsComponent) {
		if (!this.utilService.isNullOrUndefined(laneDetails)) {
			this.cargochief.removeItem(CARGOCHIEF_KEYS.IS_FULL_PROFILE_OPENED);
			this.laneDetails = Object.assign({}, laneDetails);
			this.sharedService.sendCarrierSearchEvent({ type: 'note', value: this.laneDetails });
			this.isCarrierFullProfileOpened = false;
		}
	}

	@HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
		event.stopPropagation();
		if (this.isCarrierFullProfileOpened) {
			event.stopImmediatePropagation();
		}
		this.closeCarrierFullProfile();
	}

	closeCarrierFullProfile() {
		if (!this.utilService.isNullOrUndefined(this.laneDetails)) {
			this.closeLaneDetails(this.laneDetails);
		}
	}

	fireViewedProjectsListEvent() {
		const payload = {
			user: this.utilService.getUserDataForSegmentEvent(),
		};
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_viewed_project_list, payload);
	}

	capacityNotificationClicked() {
		this.sharedService.sendfeedbackNotificationClickedEvent(true);
	}

	showMenuItem(item: string) {
		switch (item) {
			case 'bulkTools':
				return (
					this.productsService.BULK_MATCHING ||
					(environment.PRICE_LANES_BUTTON_ENABLED && this.productsService.PRICING)
				);
			case 'lanesRFP':
				return environment.PRICE_LANES_BUTTON_ENABLED && this.productsService.PRICING;
			case 'bulkMatch':
				return this.productsService.BULK_MATCHING;
			case 'team':
				return (
					(this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner()) &&
					this.utilService.isPaidUser()
				);
			case 'emailTemplates':
				return environment.EMAIL_TEMPLATES && this.accountSettings.CUSTOIZABLE_EMAILS;
			case 'carrierAutomationPage':
				return this.productsService.CARRIER_PROFILES && !this.utilService.isAccountTypeCarrier();
			default:
				return false;
		}
	}

	integrationsEnabled() {
		return (
			this.productsService.INTEGRATIONS &&
			environment.INTEGRATIONS &&
			(this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner())
		);
	}

	isAuthorizedForCustomerScorecard(): boolean {
		return (
			!this.utilService.isAccountTypeShipper() &&
			this.accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS
		);
	}

	@HostListener('document:keydown./', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
		const htmlElement = event.target as HTMLInputElement;
		if (
			!this.quickSearchComponent.isQuickSearchClicked &&
			htmlElement &&
			htmlElement.getAttribute('data-placeholder') != 'URL' &&
			htmlElement.tagName !== 'INPUT'
		) {
			this.quickSearchComponent.animate();
		}
	}

	openProfilePanel() {
		this.highlightLandingPageSection = true;
		this.profilePopoverComponent.openProfilePopover();
		setTimeout(() => {
			this.highlightLandingPageSection = false;
		}, 5000);
	}

	openNotificationPopover({ panelToOpen }) {
		this.notificationsPopoverComponent?.openNotificationPopoverWithPanelExpanded({ panelToOpen });
	}

	unsubscribeAllObservables() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.openProfilePanelSubscription)) {
			this.openProfilePanelSubscription.unsubscribe();
		}
	}

	navigateToCarrierFullProfile() {
		this.router.navigate([`carriers/${this.cargochief.CARRIER.id}`]);
	}

	isEmailRoutingEnabled(): boolean {
		return (
			this.accountSettings.EMAIL_ROUTING &&
			(this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner())
		);
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribeAllObservables();
	}
}
